<!--
  ============================================================================
  MaltaHealthLandingV3 — Versão 3 da landing do evento "Malta Health Innovation
  Circle" (SO/check · wellbeing preventive platform). Clone da
  MaltaHealthLandingV2 para iterar novas variações sem afetar a v1/v2.
  Rota: /malta-v3 · redirect do formulário → malta-v3-gracias.

  Estrutura:
    1. Hero  : Logo + Headline + Subheadline + VSL + CTA primário
    2. Topics: "What Will We Explore?" — lista com checks
    3. Experts: Dr. Francisco Mera + Dr. Luis F. Jaime
    4. Schedule: "Event Experience" — timeline de horários
    5. Who Should Attend: chips de perfis profissionais
    6. CTA final: "Request Your Invitation" (limitado a 30)

  Paleta (premium · dourada · editorial — inspirada no flyer oficial MHIC):
    - Navy profundo            #02142A
    - Dourado principal        #C9A227
    - Dourado claro/champanhe  #F2CA6B
    - Branco
    - Glow dourado + degradês tecnológicos sutis

  Hero: thumbnail de vídeo com botão de play (estilo flyer) — o iframe
  do YouTube só é carregado após o clique, com legenda "Discover MHIC /
  Watch Video" abaixo da miniatura.

  Idioma: EN. CTA → WhatsApp.
  ============================================================================
-->
<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    class="malta-page"
    :mostrar-switcher="false"
    :auto-idioma="false"
  >
    <!-- ============================================================
         1. HERO + VÍDEO
            Logo + Headline + Subheadline + VSL + CTA primário.
         ============================================================ -->
    <header class="hero">
      <div class="hero-photo hero-photo--desktop" :style="{ backgroundImage: `url(${heroBg})` }" aria-hidden="true"></div>
      <div class="hero-photo hero-photo--mobile" :style="{ backgroundImage: `url(${heroBgMobile})` }" aria-hidden="true"></div>
      <div class="hero-photo-overlay" aria-hidden="true"></div>
      <div class="hero-glow" aria-hidden="true"></div>
      <div class="hero-dna" aria-hidden="true"></div>

      <div class="hero-inner">
        <div class="hero-logo">
          <img :src="logoBranco" alt="SO/check" />
        </div>

        <h1 class="hero-titulo">
          <span class="t-line t-white">MALTA</span>
          <span class="t-line ds-texto-gradiente t-health-neon">HEALTH</span>
          <span class="t-line t-white">INNOVATION</span>
          <span class="t-circle">— C I R C L E —</span>
        </h1>

        <span class="hero-data">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          12 September, Hotel Verdala
        </span>

        <p class="hero-lead">
          The Future of
          <span class="lead-accent">Preventive Health</span>
          in Just <span class="lead-accent">5 Seconds</span>
        </p>

        <!-- VÍDEO VSL no topo da página — miniatura com botão de play
             (estilo flyer); o iframe só carrega após o clique. -->
        <div class="video-block">
          <button
            v-if="!videoPlaying"
            type="button"
            class="video-wrapper video-wrapper--thumb"
            aria-label="Play video — Discover MHIC"
            @click="videoPlaying = true"
          >
            <img :src="videoThumb" alt="" aria-hidden="true" loading="lazy" />
            <span class="video-play" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </span>
          </button>
          <div v-else class="video-wrapper" aria-label="Malta Health Innovation Circle — event video">
            <iframe
              :src="videoEmbedUrl"
              title="Malta Health Innovation Circle"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              loading="lazy"
            ></iframe>
          </div>

          <p class="video-caption">
            <span class="video-caption-titulo">Discover MHIC</span>
            <span class="video-caption-sub">Watch Video</span>
          </p>
        </div>

        <!-- CTA principal → formulário no final da página -->
        <a href="#apply" class="hero-cta">
          Request your invitation
          <svg class="hero-cta-seta" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </a>
      </div>
    </header>

    <!-- ============================================================
         1.5 WHAT YOU'LL DISCOVER — acordeão de tópicos
            Cada título abre/fecha revelando a descrição.
         ============================================================ -->
    <SecaoPadrao id="discover">
      <SecaoHeader
        titulo-antes="What You'll"
        titulo-destaque="Discover"
      />

      <div class="discover-list">
        <div
          v-for="(item, i) in discover"
          :key="i"
          class="discover-item"
          :class="{ 'is-open': isOpen(i) }"
        >
          <button
            type="button"
            class="discover-trigger"
            :aria-expanded="isOpen(i)"
            :aria-controls="`discover-panel-${i}`"
            @click="toggle(i)"
          >
            <span class="discover-num">{{ i + 1 }}</span>
            <span class="discover-heads">
              <span class="discover-titulo">{{ item.titulo }}</span>
              <span class="discover-sub">{{ item.sub }}</span>
            </span>
            <span class="discover-chevron" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </span>
          </button>
          <div :id="`discover-panel-${i}`" class="discover-panel" role="region">
            <div class="discover-panel-inner">
              <p class="discover-texto">{{ item.texto }}</p>
            </div>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         2. WHAT WILL WE EXPLORE?
            Lista com checks — temas centrais do evento.
         ============================================================ -->
    <SecaoPadrao id="explore" variante="alt">
      <SecaoHeader
        titulo-antes="What Will We"
        titulo-destaque="Explore?"
        subtitulo="A morning dedicated to innovation, preventive health and wellbeing"
      />

      <p class="explore-label">Topics include:</p>

      <ul class="explore-list">
        <li v-for="(item, i) in topicos" :key="i" class="explore-item" :class="{ 'explore-item--highlight': item.destaque }">
          <span class="explore-check" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </span>
          <span class="explore-texto">{{ item.texto }}</span>
        </li>
      </ul>
    </SecaoPadrao>

    <!-- ============================================================
         3. INVITED EXPERTS
            Cards dos especialistas convidados.
         ============================================================ -->
    <SecaoPadrao id="experts">
      <SecaoHeader
        titulo-antes="International Guest"
        titulo-destaque="Speakers"
      />

      <div class="experts-grid">
        <article v-for="expert in experts" :key="expert.nome" class="expert-card">
          <div class="expert-avatar">
            <div class="expert-avatar-ring" aria-hidden="true"></div>
            <img v-if="expert.foto" :src="expert.foto" :alt="expert.nome" loading="lazy" />
            <span v-else class="expert-iniciais" aria-hidden="true">{{ iniciais(expert.nome) }}</span>
          </div>
          <h3 class="expert-nome">{{ expert.nome }}</h3>
          <span v-if="expert.titulo" class="expert-titulo">{{ expert.titulo }}</span>
          <ul v-if="expert.credenciais.length" class="expert-credenciais">
            <li v-for="(c, i) in expert.credenciais" :key="i">
              <svg class="expert-cred-icone" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>{{ c }}</span>
            </li>
          </ul>
        </article>
      </div>

      <div class="mid-cta">
        <a href="#apply" class="hero-cta">
          Request your invitation
          <svg class="hero-cta-seta" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </a>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         4. EVENT EXPERIENCE — timeline horária
         ============================================================ -->
    <SecaoPadrao id="schedule" variante="alt">
      <SecaoHeader
        titulo-antes="Event"
        titulo-destaque="Experience"
      />

      <ol class="schedule">
        <li v-for="(slot, i) in agenda" :key="i" class="schedule-item" :class="{ 'schedule-item--demo': slot.demo }">
          <span class="schedule-time">{{ slot.hora }}</span>
          <span class="schedule-marker" aria-hidden="true"></span>
          <div class="schedule-content">
            <h3 class="schedule-titulo">{{ slot.titulo }}</h3>
            <p v-if="slot.autor" class="schedule-autor">{{ slot.autor }}</p>
            <p v-if="slot.desc" class="schedule-desc">{{ slot.desc }}</p>
          </div>
        </li>
      </ol>
    </SecaoPadrao>

    <!-- ============================================================
         5. WHO SHOULD ATTEND?
         ============================================================ -->
    <SecaoPadrao id="who">
      <SecaoHeader
        titulo-antes="Who Should"
        titulo-destaque="Attend?"
      />

      <div class="perfil-chips">
        <span v-for="p in perfis" :key="p" class="perfil-chip">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ p }}
        </span>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         6. CTA FINAL → WhatsApp (Request Your Invitation)
         ============================================================ -->
    <SecaoPadrao id="apply" variante="alt" class="secao-cta-final">
      <div class="cta-final">
        <h2 class="cta-final-titulo">
          Become Part of the <span class="ds-texto-gradiente">Circle</span>
        </h2>
        <p class="cta-final-desc">
          Request your invitation and benefit from our exclusive
          <strong>Early Bird</strong> offer
        </p>
      </div>

      <div class="cta-final-form">
        <FormularioLead
          idioma="en"
          pais-padrao="MT"
          :active-campaign="acConfig"
          :redirect-to="{ name: 'malta-v3-gracias' }"
          :mostrar-header="false"
          :profissoes="profissoesFormulario"
          texto-botao="Request your invitation"
          @submitted="onLeadSubmitted"
        />
      </div>
    </SecaoPadrao>

    <template #rodape>
      <RodapeSite
        marca="SO/check"
        direitos="All rights reserved."
      />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import FormularioLead from '@/components/global/formulario/FormularioLead.vue'
import { sendLeadToWebhook } from '@/lib/webhookLead'
import logoBranco from '@/assets/images/logo-branco.svg'
import heroBg from '@/assets/images/malta-health/hero-bg-v2.png'
import heroBgMobile from '@/assets/images/malta-health/hero-bg-v2-mobile.png'
import jacquelineCervantes from '@/assets/images/malta-health/jacqueline-cervantes.png'
import drFranciscoMera from '@/assets/images/malta-health/dr-francisco-mera.png'
import drLuisJaime from '@/assets/images/malta-health/dr-luis-jaime.png'

/* Idioma fixo: o evento é apresentado em inglês (conteúdo do flyer). */
const currentLang = ref('en')

/* Título da aba — esta página não usa useSeo, então garantimos o
   <title> correto ao montar (evita herdar o título padrão do index.html). */
onMounted(() => {
  document.title = 'Malta Health Innovation Circle'
})

/* ============================================================
   VÍDEO DO EVENTO (VSL)
   URL fornecida: https://youtu.be/tRlLYGvOE9Y
   ------------------------------------------------------------
   Estilo flyer: exibe a miniatura do YouTube com botão de play;
   o iframe (com autoplay) só é montado após o clique do usuário.
   ============================================================ */
const VIDEO_ID = 'tRlLYGvOE9Y'
const videoEmbedUrl = `https://www.youtube.com/embed/${VIDEO_ID}?rel=0&controls=1&autoplay=1`
const videoThumb = `https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`
const videoPlaying = ref(false)

/* ============================================================
   FORMULÁRIO DE CAPTURA
   ------------------------------------------------------------
   ActiveCampaign — form dedicado do evento Malta (form_45).
   Campos custom: field[1] = profissão · field[2] = país.
   ============================================================ */
const acConfig = {
  endpoint: 'https://eduardgasset95711.activehosted.com/proc.php',
  u: '45',
  f: '45',
  s: '',
  c: '0',
  m: '0',
  act: 'sub',
  v: '2',
  or: 'e9504938-7768-4205-b527-6252971ad56c'
}

/* Opções de profissão do formulário do evento Malta. */
const profissoesFormulario = [
  'Doctor',
  'Nutritionist',
  'Pharmacist',
  'Other healthcare professional'
]

/* Ao submeter: envia o lead ao webhook do Make (Meta CAPI server-side).
   O redirect para a página de obrigado é feito pelo próprio FormularioLead. */
const onLeadSubmitted = (payload) => {
  sendLeadToWebhook(payload, {
    contentName: 'malta_health_circle',
    contentCategory: 'evento_malta',
    currency: 'EUR'
  })
}

/* ============================================================
   WHAT YOU'LL DISCOVER — acordeão
   ------------------------------------------------------------
   Estado: múltiplos itens podem ficar abertos simultaneamente.
   ============================================================ */
const openSet = ref(new Set())
const isOpen = (i) => openSet.value.has(i)
const toggle = (i) => {
  const next = new Set(openSet.value)
  next.has(i) ? next.delete(i) : next.add(i)
  openSet.value = next
}

const discover = [
  {
    titulo: 'Heavy Metals & Human Health',
    sub: 'The Invisible Challenge to Modern Health',
    texto: 'Heavy metals are part of our everyday environment and may accumulate in the body over time. This session explores how environmental exposure can influence essential biological processes and why understanding this topic is becoming increasingly important in preventive healthcare and long-term wellbeing.'
  },
  {
    titulo: 'Nutrient Absorption: Beyond Supplementation',
    sub: 'Why Taking More Isn’t Always the Answer',
    texto: 'Optimal health depends not only on what we consume, but on what our bodies are able to absorb and utilize. Discover why nutritional deficiencies may persist despite supplementation and how mineral balance plays a fundamental role in supporting the body’s natural functions.'
  },
  {
    titulo: 'The Importance of Minerals in Preventive Health',
    sub: 'Small Elements. Extraordinary Impact.',
    texto: 'Minerals and trace elements are involved in hundreds of physiological processes that support energy production, immune function, cognitive performance and overall wellbeing. Learn why maintaining their balance is one of the foundations of preventive healthcare.'
  },
  {
    titulo: 'Oxidative Stress: Understanding Cellular Balance',
    sub: 'One of the Most Important Concepts in Modern Preventive Medicine',
    texto: 'Oxidative stress is widely studied for its relationship with cellular aging and overall health. This session will explore how maintaining cellular balance supports long-term wellbeing and why it has become a key focus in preventive and longevity medicine.'
  },
  {
    titulo: 'Personalized Preventive Healthcare',
    sub: 'Because Every Body Tells a Different Story',
    texto: 'No two individuals share the same biological profile. Advances in preventive healthcare now allow a more personalized understanding of nutritional status, lifestyle and individual needs, supporting more informed decisions for long-term health.'
  },
  {
    titulo: 'Beauty from Within: The Science Behind Healthy Skin, Hair & Nails',
    sub: 'True Beauty Begins at the Cellular Level',
    texto: 'Healthy skin, stronger hair and resilient nails reflect much more than external care. Discover how minerals, trace elements and nutritional balance support the body’s natural regenerative processes, reinforcing the connection between internal health, vitality and aesthetic wellbeing.'
  },
  {
    titulo: 'OligoCheck Experience',
    sub: 'Discover Your Health Through Advanced Technology',
    texto: 'Every participant will experience a live OligoCheck assessment and discover how advanced spectrophotometric technology can provide valuable insights into mineral status, trace elements and heavy metal exposure. The assessment is performed directly on the palm of the hand, providing an intracellular analysis in a fast, non-invasive and personalized way, with results available in just 5 seconds.'
  }
]

/* ============================================================
   CONTEÚDO — extraído diretamente do briefing.
   ============================================================ */
const topicos = [
  { texto: 'The role of trace elements in health and wellbeing' },
  { texto: 'Heavy metals and environmental exposure' },
  { texto: 'Oxidative stress and healthy aging' },
  { texto: 'Preventive health strategies' },
  { texto: 'Wellness optimisation' },
  { texto: 'Innovative technologies in healthcare' },
  { texto: 'LIVE OLIGOCHECK DEMONSTRATION', destaque: true },
  { texto: 'Real clinical applications and case discussions' }
]

/* Iniciais para avatar de fallback quando não há foto do speaker. */
const iniciais = (nome) => {
  const limpo = nome.replace(/^(Dr\.?|Dra\.?)\s+/i, '')
  return limpo
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
}

const experts = [
  {
    nome: 'Jacqueline Cervantes',
    titulo: 'Founder · Malta Health Innovation Circle',
    foto: jacquelineCervantes,
    credenciais: [
      'MSc in Functional Nutrition & Psychoneuroimmunology',
      'C-Level So/Check Platform'
    ]
  },
  {
    nome: 'Dr. Francisco Mera',
    titulo: 'Clinical Researcher',
    foto: drFranciscoMera,
    credenciais: [
      'Expert in Lifestyle Medicine & Healthy Longevity',
      'Specialist in Precision Medicine, Epigenetics & Immunomodulation',
      'Co-founder of LongeVia & Longevity Academy',
      'Ambassador · So/Check Platform'
    ]
  },
  {
    nome: 'Dr. Luis Jaime',
    titulo: 'Specialist in Longevity Medicine',
    foto: drLuisJaime,
    credenciais: [
      'Medical Doctor & Specialist in Longevity Medicine',
      'Expert in Regenerative, Anti-Aging & Precision Medicine',
      'Founder & CEO of The Longevity Experts Group',
      'Founder & Medical Director of Advanced Biohacking (Dubai)',
      'Founder & Medical Director of BioDrip'
    ]
  }
]

const agenda = [
  {
    hora: '09:00 – 09:45',
    titulo: 'Welcome & Registration',
    desc: 'Arrival, registration and welcome to the Malta Health Innovation Circle.'
  },
  {
    hora: '10:00 – 10:30',
    titulo: 'Guided Breathwork Experience',
    desc: 'A guided session designed to regulate the nervous system, improve focus and prepare body and mind for a transformative day of learning.'
  },
  {
    hora: '10:45 – 11:00',
    titulo: 'Opening Remarks',
    autor: 'Jacqueline Cervantes · Founder of the Malta Health Innovation Circle',
    desc: 'A brief introduction to the vision behind the event and why preventive healthcare is becoming the new global standard.'
  },
  {
    hora: '11:00 – 11:45',
    titulo: 'Beyond Symptoms: The New Era of Preventive Medicine',
    autor: 'Dr. Francisco Mera',
    desc: 'Heavy metals, nutrient absorption and the foundations of modern preventive healthcare.'
  },
  {
    hora: '11:45 – 13:15',
    titulo: 'Wellness Breakfast, Networking & OligoCheck Assessments',
    desc: 'Enjoy a curated healthy breakfast while connecting with physicians, healthcare professionals and wellness innovators. During this session, all participants will receive their personalized OligoCheck® assessment, providing valuable intracellular insights into mineral status, trace elements and heavy metal exposure.'
  },
  {
    hora: '13:15 – 14:00',
    titulo: 'Clinical Insights: From Data to Personalized Interventions',
    autor: 'Dr. Luis Jaime',
    desc: 'Discover how precision medicine, longevity and personalized healthcare strategies are transforming the future of clinical practice.'
  },
  {
    hora: '14:00 – 14:45',
    titulo: 'Live OligoCheck® Experience',
    demo: true,
    desc: 'A live demonstration of the OligoCheck® assessment, including real-time interpretation of results and practical clinical applications presented by our medical experts.'
  },
  {
    hora: '14:45 – 15:00',
    titulo: 'Closing Circle',
    desc: 'Final reflections, key takeaways and an invitation to become part of the Malta Health Innovation Circle community.'
  }
]

const perfis = [
  'Physicians',
  'Functional & Integrative Medicine Practitioners',
  'Nutritionists & Dietitians',
  'Pharmacists',
  'Dentists',
  'Healthcare Professionals',
  'Wellness & Longevity Experts',
  'Clinic & Healthcare Business Owners',
  'Health Entrepreneurs',
  'Professionals passionate about Preventive Healthcare'
]
</script>

<style scoped>
/* ============================================================
   PALETA LOCAL — deep navy + dourado (inspirada no flyer oficial MHIC)
     · Navy profundo            #02142A
     · Dourado principal        #C9A227
     · Dourado claro/champanhe  #F2CA6B
     · Branco
   Custom properties cascateiam para badges, FeatureCard,
   gradiente de texto, rodapé etc.
   ============================================================ */
.app-shell--produto {
  --ds-accent: #C9A227;
  --ds-accent-2: #F2CA6B;
  --ds-accent-soft: rgba(201, 162, 39, 0.12);
  --ds-accent-border: rgba(242, 202, 107, 0.32);
  --ds-dourado: #F2CA6B;
  --ds-dourado-soft: rgba(242, 202, 107, 0.12);
  --ds-gradient-text: linear-gradient(90deg, #F2CA6B, #C9A227, #F2CA6B);
}

/* ============================================================
   HERO
   ============================================================ */
.hero {
  position: relative;
  overflow: hidden;
  padding: 56px var(--ds-s-5) var(--ds-s-11);
  background:
    radial-gradient(ellipse 900px 700px at 75% 12%, rgba(201, 162, 39, 0.22) 0%, transparent 55%),
    radial-gradient(ellipse 700px 500px at 12% 80%, rgba(242, 202, 107, 0.10) 0%, transparent 60%),
    linear-gradient(180deg, #02142A 0%, #010d18 60%);
}

/* Foto de fundo do hero (v2) */
.hero-photo {
  position: absolute;
  inset: 0;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
  filter: brightness(1.05) contrast(1.1) saturate(1.15);
}

/* Tint azul forte — aplicado em cima da foto (desktop + mobile) via
   mix-blend-mode: multiply, para dar um tom de azul evidente sem
   perder o detalhe da imagem por trás. */
.hero-photo::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(165deg, #0B5FD6 0%, #0A3D8F 45%, #041638 100%);
  mix-blend-mode: multiply;
  opacity: 0.72;
  pointer-events: none;
}

/* Alterna a foto de fundo do hero entre desktop e mobile */
.hero-photo--mobile { display: none; }

@media (max-width: 720px) {
  .hero-photo--desktop { display: none; }
  .hero-photo--mobile {
    display: block;
    background-position: center top;
  }

  /* Escurecimento leve no mobile, só o suficiente para a leitura do
     texto/vídeo/CTA — o tom azul forte já vem da camada ::after. */
  .hero-photo-overlay {
    background:
      linear-gradient(180deg,
        rgba(1, 13, 24, 0.08) 0%,
        rgba(1, 13, 24, 0.16) 42%,
        rgba(1, 13, 24, 0.55) 100%);
  }
}

/* Overlay para garantir contraste/legibilidade sobre a foto */
.hero-photo-overlay {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 900px 700px at 75% 12%, rgba(201, 162, 39, 0.06) 0%, transparent 55%),
    linear-gradient(180deg, rgba(2, 20, 42, 0.05) 0%, rgba(1, 13, 24, 0.16) 78%, rgba(1, 13, 24, 0.48) 100%);
}

.hero-glow {
  position: absolute;
  top: -120px;
  right: -120px;
  width: 520px;
  height: 520px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201, 162, 39, 0.32) 0%, transparent 65%);
  filter: blur(20px);
  pointer-events: none;
  z-index: 0;
}

.hero-dna {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 80% 30%, rgba(242, 202, 107, 0.12) 0, transparent 2px),
    radial-gradient(circle at 88% 55%, rgba(242, 202, 107, 0.10) 0, transparent 2px),
    radial-gradient(circle at 72% 70%, rgba(242, 202, 107, 0.10) 0, transparent 2px);
  background-size: 140px 140px;
  opacity: 0.6;
  pointer-events: none;
  z-index: 0;
}

.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
  animation: heroFadeInUp 0.8s ease-out;
}

.hero-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: var(--ds-s-6);
}

.hero-logo img {
  height: 27px;
}

/* Título empilhado estilo flyer */
.hero-titulo {
  font-family: var(--ds-font-display);
  margin: 0 0 var(--ds-s-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 0.98;
  letter-spacing: 1px;
}

.t-line {
  font-size: clamp(32px, 8.8vw, 69px);
  text-transform: uppercase;
}

/* Sombra sutil para manter a legibilidade do título agora que a foto
   de fundo aparece mais (overlay mais leve). */
.t-white {
  color: var(--ds-text);
  text-shadow: 0 2px 14px rgba(1, 13, 24, 0.55);
}

/* HEALTH em destaque — brilho dourado pulsante sobre o texto em gradiente */
.t-health-neon {
  filter:
    drop-shadow(0 0 4px rgba(242, 202, 107, 0.55))
    drop-shadow(0 0 12px rgba(201, 162, 39, 0.38));
  animation: healthNeonPulse 2.6s ease-in-out infinite;
}

@keyframes healthNeonPulse {
  0%, 100% {
    filter:
      drop-shadow(0 0 4px rgba(242, 202, 107, 0.55))
      drop-shadow(0 0 12px rgba(201, 162, 39, 0.38));
  }
  50% {
    filter:
      drop-shadow(0 0 6px rgba(242, 202, 107, 0.7))
      drop-shadow(0 0 18px rgba(201, 162, 39, 0.5));
  }
}

@media (prefers-reduced-motion: reduce) {
  .t-health-neon { animation: none; }
}

.t-circle {
  font-size: clamp(11px, 2.7vw, 18px);
  letter-spacing: 6px;
  color: var(--ds-text-muted);
  margin-top: 6px;
}

.hero-data {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  margin: var(--ds-s-5) 0 var(--ds-s-3);
  padding: 8px var(--ds-s-4);
  border-radius: var(--ds-r-full);
  font-size: 14px;
  font-family: var(--ds-font-bold);
  letter-spacing: 0.5px;
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  color: var(--ds-accent-2);
}

.hero-data svg { flex-shrink: 0; }

.hero-lead {
  font-family: var(--ds-font-bold);
  font-size: clamp(20px, 3.4vw, 28px);
  color: var(--ds-text);
  text-shadow: 0 2px 14px rgba(1, 13, 24, 0.55);
  margin: 0 0 var(--ds-s-8);
  line-height: 1.25;
}

.lead-accent {
  background: var(--ds-gradient-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ============================================================
   VÍDEO VSL — miniatura estilo flyer + botão de play
   ============================================================ */
.video-block {
  margin: 0 auto var(--ds-s-8);
  max-width: 760px;
}

.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-accent-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(201, 162, 39, 0.14),
    0 0 60px -10px rgba(242, 202, 107, 0.38);
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* Miniatura clicável (botão) — mostra a capa do YouTube até o clique */
.video-wrapper--thumb {
  display: block;
  padding: 0;
  cursor: pointer;
  transition: box-shadow var(--ds-t-base), transform var(--ds-t-base);
}

.video-wrapper--thumb:hover {
  transform: translateY(-2px);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(242, 202, 107, 0.28),
    0 0 70px -8px rgba(242, 202, 107, 0.5);
}

.video-wrapper--thumb img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.82) saturate(1.05);
}

.video-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1a1204;
  background: linear-gradient(135deg, #F2CA6B 0%, #C9A227 100%);
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.55), 0 0 0 6px rgba(255, 255, 255, 0.12);
  transition: transform var(--ds-t-base), box-shadow var(--ds-t-base);
}

.video-wrapper--thumb:hover .video-play {
  transform: translate(-50%, -50%) scale(1.08);
  box-shadow: 0 10px 28px -4px rgba(0, 0, 0, 0.6), 0 0 0 6px rgba(255, 255, 255, 0.18);
}

/* Legenda "Discover MHIC · Watch Video" abaixo da miniatura */
.video-caption {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: var(--ds-s-3) 0 0;
  font-size: 14px;
}

.video-caption-titulo {
  font-family: var(--ds-font-bold);
  color: var(--ds-text);
}

.video-caption-sub {
  font-family: var(--ds-font-bold);
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ds-accent-2);
  position: relative;
  padding-left: 12px;
}

.video-caption-sub::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--ds-accent-2);
}

/* ============================================================
   CTA (hero + final) — botão WhatsApp
   ============================================================ */
.hero-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-s-3);
  font-family: var(--ds-font-bold);
  font-size: 16px;
  padding: 16px 28px;
  border-radius: var(--ds-r-full);
  text-decoration: none;
  color: #1a1204;
  background: linear-gradient(135deg, #F2CA6B 0%, #C9A227 60%, #8a6a1a 100%);
  box-shadow: 0 10px 28px -6px rgba(201, 162, 39, 0.55);
  transition: all var(--ds-t-base);
}

.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px -8px rgba(242, 202, 107, 0.75);
}

/* CTA intermediário (meio da página) */
.mid-cta {
  display: flex;
  justify-content: center;
  margin-top: var(--ds-s-8);
}

@media (max-width: 720px) {
  .mid-cta .hero-cta { width: 100%; }
}

.hero-cta-icone { display: inline-flex; }
.hero-cta-seta { transition: transform var(--ds-t-base); }
.hero-cta:hover .hero-cta-seta { transform: translateX(4px); }

/* ============================================================
   DISCOVER — acordeão (What You'll Discover)
   ============================================================ */
.discover-list {
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--ds-s-3);
}

.discover-item {
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  overflow: hidden;
  transition: border-color var(--ds-t-base), background var(--ds-t-base), box-shadow var(--ds-t-base);
}

.discover-item:hover {
  border-color: var(--ds-accent-border);
}

.discover-item.is-open {
  border-color: rgba(242, 202, 107, 0.45);
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.10), rgba(242, 202, 107, 0.04));
  box-shadow: 0 0 0 1px rgba(242, 202, 107, 0.14), 0 10px 30px -14px rgba(201, 162, 39, 0.4);
}

.discover-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--ds-s-4);
  padding: 18px 20px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font-family: inherit;
}

.discover-num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--ds-font-bold);
  font-size: 15px;
  color: var(--ds-accent-2);
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  transition: all var(--ds-t-base);
}

.discover-item.is-open .discover-num {
  background: var(--ds-accent);
  color: #fff;
  border-color: var(--ds-accent);
  box-shadow: 0 0 16px rgba(242, 202, 107, 0.5);
}

.discover-heads {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.discover-titulo {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  color: var(--ds-text);
  line-height: 1.35;
}

.discover-sub {
  font-size: 13px;
  color: var(--ds-accent-2);
  line-height: 1.35;
}

.discover-chevron {
  flex-shrink: 0;
  display: inline-flex;
  color: var(--ds-text-muted);
  transition: transform var(--ds-t-base), color var(--ds-t-base);
}

.discover-item.is-open .discover-chevron {
  transform: rotate(180deg);
  color: var(--ds-accent-2);
}

/* Painel expansível (truque grid-template-rows 0fr → 1fr) */
.discover-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--ds-t-smooth);
}

.discover-item.is-open .discover-panel {
  grid-template-rows: 1fr;
}

.discover-panel-inner {
  overflow: hidden;
}

.discover-texto {
  margin: 0;
  padding: 0 20px 20px 68px;
  font-size: 15px;
  color: var(--ds-text-muted);
  line-height: 1.6;
}

@media (max-width: 600px) {
  .discover-texto { padding-left: 20px; }
}

/* ============================================================
   EXPLORE — lista com checks (What Will We Explore?)
   ============================================================ */
.explore-label {
  max-width: 880px;
  margin: 0 auto var(--ds-s-4);
  font-family: var(--ds-font-bold);
  font-size: 15px;
  color: var(--ds-text);
}

.explore-list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 880px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--ds-s-3);
}

.explore-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-md);
  transition: all var(--ds-t-base);
}

.explore-item:hover {
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
  transform: translateY(-2px);
}

.explore-item--highlight {
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.16), rgba(242, 202, 107, 0.08));
  border-color: rgba(242, 202, 107, 0.45);
  box-shadow: 0 0 0 1px rgba(242, 202, 107, 0.18), 0 8px 28px -10px rgba(201, 162, 39, 0.4);
}

.explore-item--highlight .explore-texto {
  font-family: var(--ds-font-bold);
  color: var(--ds-text);
  letter-spacing: 0.5px;
}

.explore-check {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--ds-accent-soft);
  color: var(--ds-accent-2);
  border: 1px solid var(--ds-accent-border);
}

.explore-item--highlight .explore-check {
  background: var(--ds-accent);
  color: #fff;
  border-color: var(--ds-accent);
  box-shadow: 0 0 16px rgba(242, 202, 107, 0.55);
}

.explore-texto {
  font-size: 15px;
  color: var(--ds-text-muted);
  line-height: 1.45;
}

/* ============================================================
   EXPERTS — cards
   ============================================================ */
.experts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--ds-s-5);
  max-width: 960px;
  margin: 0 auto;
  align-items: stretch;
}

.expert-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  padding: 32px 26px;
  text-align: center;
  transition: all var(--ds-t-base);
  overflow: hidden;
  height: 100%;
}

.expert-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--ds-accent), transparent);
  opacity: 0.5;
}

.expert-card:hover {
  transform: translateY(-4px);
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
  box-shadow: 0 16px 40px -16px rgba(201, 162, 39, 0.35);
}

.expert-avatar {
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  margin: 0 auto var(--ds-s-5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: linear-gradient(135deg, #F2CA6B 0%, #C9A227 60%, #02142A 100%);
  box-shadow:
    0 12px 32px -10px rgba(201, 162, 39, 0.55),
    0 0 0 1px rgba(242, 202, 107, 0.18),
    0 0 28px -4px rgba(242, 202, 107, 0.35);
  transition: all var(--ds-t-base);
}

.expert-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  background: var(--ds-surface-2);
  display: block;
}

/* Avatar de fallback (iniciais) quando o speaker não tem foto */
.expert-iniciais {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 30% 25%, #0a2540 0%, #02142A 70%);
  font-family: var(--ds-font-display);
  font-size: 40px;
  letter-spacing: 1px;
  color: var(--ds-accent-2);
  text-shadow: 0 0 18px rgba(242, 202, 107, 0.5);
}

.expert-avatar-ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1px solid rgba(242, 202, 107, 0.25);
  pointer-events: none;
  opacity: 0.8;
  transition: all var(--ds-t-base);
}

.expert-card:hover .expert-avatar {
  transform: scale(1.04);
  box-shadow:
    0 18px 42px -10px rgba(201, 162, 39, 0.7),
    0 0 0 1px rgba(242, 202, 107, 0.28),
    0 0 38px -2px rgba(242, 202, 107, 0.55);
}

.expert-card:hover .expert-avatar-ring {
  inset: -10px;
  opacity: 1;
  border-color: rgba(242, 202, 107, 0.45);
}

.expert-nome {
  font-family: var(--ds-font-bold);
  font-size: 20px;
  color: var(--ds-text);
  margin: 0 0 var(--ds-s-3);
}

.expert-titulo {
  display: inline-block;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ds-accent-2);
  font-family: var(--ds-font-bold);
  padding: 6px 14px;
  border-radius: var(--ds-r-full);
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  line-height: 1.3;
}

.expert-credenciais {
  list-style: none;
  margin: var(--ds-s-5) 0 0;
  padding: var(--ds-s-5) 0 0;
  width: 100%;
  border-top: 1px solid var(--ds-border-subtle);
  display: flex;
  flex-direction: column;
  gap: var(--ds-s-3);
  text-align: left;
}

.expert-credenciais li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  color: var(--ds-text-muted);
  line-height: 1.45;
}

.expert-cred-icone {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--ds-accent-2);
}

/* ============================================================
   SCHEDULE — timeline horária
   ============================================================ */
.schedule {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 720px;
  position: relative;
}

.schedule::before {
  content: '';
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 116px;
  width: 2px;
  background: linear-gradient(180deg, var(--ds-accent), rgba(242, 202, 107, 0.2));
}

.schedule-item {
  position: relative;
  display: grid;
  grid-template-columns: 104px 24px 1fr;
  gap: 12px;
  align-items: flex-start;
  padding: var(--ds-s-3) 0;
}

.schedule-item + .schedule-item { padding-top: var(--ds-s-5); }

.schedule-time {
  font-family: var(--ds-font-bold);
  font-size: 14px;
  line-height: 1.4;
  color: var(--ds-accent-2);
  letter-spacing: 0.2px;
  text-align: right;
  padding-top: 0;
  white-space: nowrap;
}

.schedule-marker {
  position: relative;
  width: 14px;
  height: 14px;
  margin-top: 3px;
  margin-left: 5px;
  border-radius: 50%;
  background: var(--ds-bg);
  border: 2px solid var(--ds-accent);
  box-shadow: 0 0 0 4px rgba(201, 162, 39, 0.12);
  z-index: 1;
}

.schedule-item--demo .schedule-marker {
  background: var(--ds-accent);
  box-shadow: 0 0 0 4px rgba(242, 202, 107, 0.22), 0 0 18px rgba(242, 202, 107, 0.6);
}

.schedule-content {
  padding: 4px 0;
}

.schedule-titulo {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  color: var(--ds-text);
  margin: 0 0 2px;
  line-height: 1.4;
}

.schedule-autor {
  margin: 0;
  font-size: 14px;
  font-family: var(--ds-font-bold);
  color: var(--ds-accent-2);
}

.schedule-desc {
  margin: 6px 0 0;
  font-size: 14px;
  color: var(--ds-text-muted);
  line-height: 1.55;
}

/* ============================================================
   PERFIL — chips
   ============================================================ */
.perfil-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--ds-s-3);
  max-width: 820px;
  margin: 0 auto;
}

.perfil-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px var(--ds-s-5);
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-full);
  font-size: 14px;
  color: var(--ds-text-muted);
  transition: all var(--ds-t-base);
}

.perfil-chip:hover {
  border-color: var(--ds-accent-border);
  color: var(--ds-text);
  background: var(--ds-surface-3);
}

.perfil-chip svg { color: var(--ds-accent); flex-shrink: 0; }

/* ============================================================
   CTA FINAL
   ============================================================ */
.secao-cta-final { text-align: center; }

.cta-final {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cta-final-titulo {
  font-family: var(--ds-font-display);
  font-size: clamp(26px, 5vw, 38px);
  line-height: 1.2;
  margin: 0 0 var(--ds-s-4);
  color: var(--ds-text);
}

.cta-final-desc {
  font-size: 16px;
  color: var(--ds-text-muted);
  line-height: 1.6;
  margin: 0 0 var(--ds-s-6);
  max-width: 600px;
}

.cta-final-desc strong { color: var(--ds-accent-2); font-family: var(--ds-font-bold); }

.cta-final-form {
  margin-top: var(--ds-s-7);
}

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 720px) {
  .hero {
    padding: 40px var(--ds-s-4) var(--ds-s-9);
    /* Hero ocupa a tela toda no mobile para permitir centralizar a headline
       e empurrar o vídeo para baixo. */
    min-height: 100svh;
    display: flex;
  }

  .hero-inner {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* Grupo logo → subheadline centralizado verticalmente; o vídeo e o CTA
     descem para a parte inferior da seção, com uma boa distância acima. */
  .hero-logo { margin-top: auto; }
  .hero-lead { margin-bottom: auto; }

  .video-block {
    margin-top: var(--ds-s-9);
    margin-bottom: var(--ds-s-6);
  }
  .video-wrapper { border-radius: var(--ds-r-lg); }
  .hero-cta { width: 100%; font-size: 15px; }

  .schedule::before { left: 98px; }
  .schedule-item { grid-template-columns: 88px 18px 1fr; gap: 10px; }
  .schedule-time { font-size: 12px; }
}

@keyframes heroFadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
